import React, { useEffect, useState } from "react";
import { apptApi, patientApi, apiError } from "../../api/lab";
import { Button, Field } from "../../Components/UI/ui";
import { fullName, phoneOf, initials, GENDERS, fmtDate } from "../../lib/lab";

// Front-desk patient search (§3.4): exact 10-digit phone, or name among
// patients this lab has seen. Falls back to creating a patient inline.
const PatientPicker = ({ value, onChange }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [err, setErr] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", gender: "Male", dateOfBirth: "", age: "" });
  const [saving, setSaving] = useState(false);

  // useEffect(() => {
  //   const t = query.trim();
  //   const digits = t.replace(/\D/g, "");
  //   if (digits.length === 10) {
  //     run({ phone: digits });
  //   } else if (t.length >= 2 && !/^\d+$/.test(t)) {
  //     const h = setTimeout(() => run({ q: t }), 300);
  //     return () => clearTimeout(h);
  //   } else {
  //     setResults([]);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [query]);

  useEffect(() => {
    const t = query.trim();
    const digits = t.replace(/\D/g, "");
    const isDigitsOnly = /^\d+$/.test(t);

    if (isDigitsOnly && digits.length >= 3) {
      // Partial or full phone number, 3+ digits.
      const h = setTimeout(() => run({ phone: digits }), 300);
      return () => clearTimeout(h);
    } else if (!isDigitsOnly && t.length >= 3) {
      // Partial or full name, 3+ characters.
      const h = setTimeout(() => run({ q: t }), 300);
      return () => clearTimeout(h);
    } else {
      setResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const run = async (params) => {
    setSearching(true); setErr(null);
    try {
      setResults(await apptApi.lookupPatients(params));
    } catch (e) {
      setErr(apiError(e)); setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const createPatient = async () => {
    setErr(null);
    const phone = form.phone.replace(/\D/g, "").slice(-10);
    if (!form.firstName.trim()) return setErr("First name is required");
    if (phone.length !== 10) return setErr("Phone must be 10 digits");
    if (!form.dateOfBirth && !form.age) return setErr("Date of birth or age is required");
    setSaving(true);
    try {
      const body = {
        name: { firstName: form.firstName.trim(), lastName: form.lastName.trim() },
        contact: { phoneNumber: { countryCode: "+91", phoneNumber: phone } },
        gender: form.gender,
      };
      if (form.dateOfBirth) body.dateOfBirth = form.dateOfBirth;
      else body.age = Number(form.age);
      const created = await patientApi.create(body);
      const p = created?.patient ?? created;
      onChange(p);
      setCreating(false);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (value) {
    return (
      <div className="lab-list__item selected">
        <div className="lab-row">
          <span className="lab-avatar">{initials(fullName(value.name))}</span>
          <div>
            <div style={{ fontWeight: 600 }}>{fullName(value.name)}</div>
            <div className="lab-muted">{phoneOf(value.contact)} · {value.gender || "—"} · {value.patientId || ""}</div>
          </div>
        </div>
        <Button size="sm" onClick={() => onChange(null)}>Change</Button>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {!creating && (
        <>
          <div className="lab-row">
            <input
              className="lab-input grow"
              style={{ flex: 1 }}
              placeholder="Search by 10-digit phone or patient name's"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <Button onClick={() => { setCreating(true); setForm((f) => ({ ...f, phone: /^\d{10}$/.test(query.trim()) ? query.trim() : f.phone })); }}>
              + New patient
            </Button>
          </div>
          {searching && <span className="lab-muted">Searching…</span>}
          {results.length > 0 && (
            <div className="lab-list">
              {results.map((p) => (
                <div key={p._id} className="lab-list__item" onClick={() => onChange(p)}>
                  <div className="lab-row">
                    <span className="lab-avatar">{initials(fullName(p.name))}</span>
                    <div>
                      <div style={{ fontWeight: 600 }}>{fullName(p.name)}</div>
                      <div className="lab-muted">{phoneOf(p.contact)} · {p.gender || "—"}{p.dateOfBirth ? ` · ${fmtDate(p.dateOfBirth)}` : ""}</div>
                    </div>
                  </div>
                  <span className="lab-muted">{p.patientId}</span>
                </div>
              ))}
            </div>
          )}
          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <span className="lab-muted">No match. Name search only covers patients this lab has seen — try the phone number, or add a new patient.</span>
          )}
        </>
      )}

      {creating && (
        <div className="lab-section">
          <h4>New patient</h4>
          <div className="lab-grid cols-3">
            <Field label="First name"><input className="lab-input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></Field>
            <Field label="Last name"><input className="lab-input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></Field>
            <Field label="Phone (10 digits)"><input className="lab-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Gender">
              <select className="lab-select" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                {GENDERS.map((g) => <option key={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Date of birth"><input type="date" className="lab-input" value={form.dateOfBirth} onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} /></Field>
            <Field label="or Age"><input type="number" min="0" className="lab-input" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} /></Field>
          </div>
          <div className="lab-row end" style={{ marginTop: 12 }}>
            <Button onClick={() => setCreating(false)}>Back to search</Button>
            <Button variant="primary" loading={saving} onClick={createPatient}>Create patient</Button>
          </div>
        </div>
      )}
      {err && <span className="lab-danger-text">{err}</span>}
    </div>
  );
};

export default PatientPicker;
